import React from "react";
import { Box, Button, Flex, Heading, Text } from "@radix-ui/themes";
import { I18nContext } from "@/i18n";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  // Class components can't call the `useI18n` hook, so the translated strings
  // are read directly from the context that `useI18n` itself relies on.
  static contextType = I18nContext;
  declare context: React.ContextType<typeof I18nContext>;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      // Fall back to English if, somehow, no I18nProvider is present above this boundary.
      const t = this.context?.t;
      return (
        <Box style={{ padding: "var(--space-6)" }}>
          <Flex direction="column" gap="4" align="center" style={{ maxWidth: 480, margin: "0 auto" }}>
            <Heading size="6">{t ? t("errorBoundary.title") : "Something went wrong"}</Heading>
            <Text color="gray" align="center">
              {t ? t("errorBoundary.message") : "An unexpected error occurred. Please reload the page."}
            </Text>
            <Button size="3" onClick={this.handleReload}>
              {t ? t("errorBoundary.reload") : "Reload"}
            </Button>
          </Flex>
        </Box>
      );
    }

    return this.props.children;
  }
}
